import React, { useContext, memo, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowDown, faCircleArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { get, isNull } from 'lodash';

import { SocketContext } from '../../../../socketProvider';
import { inRange, socketEventNames, MAX_ZOOM } from '../../helper';
import { updatePath, updateCornerValue } from '../../actions';
import camMap from '../../mapLogic';

import style from './style.module.scss';

/**
 * Letter seats: (letterIndex * 1000) + number, A=1 … Z=26.
 * Bidirectional — label↔number. Plain seats (< 1000) pass through.
 * MUST stay identical to app srsButton convertSeat (encode/decode same way).
 *
 * Label → number:
 *   "A"    → 1000
 *   "B"    → 2000
 *   "Z"    → 26000
 *   "1A"   → 1001
 *   "11A"  → 1011
 *   "21A"  → 1021
 *   "4B"   → 2004
 *   "23B"  → 2023
 *   "28C"  → 3028
 *   "10Z"  → 26010
 *   "a"    → 1000   (case-insensitive)
 *   " 11a "→ 1011   (trim)
 *
 * Number → label:
 *   1000  → "A"
 *   1001  → "1A"
 *   1011  → "11A"
 *   1021  → "21A"
 *   2004  → "4B"
 *   2023  → "23B"
 *   3028  → "28C"
 *   26010 → "10Z"
 *
 * Plain (unchanged):
 *   -1, 0, 1, 2, 11, 21, 99, 111, 999
 *
 * Unchanged / invalid (returned as-is):
 *   null, "", "11AA", "A21", "21 A", "10.5", true
 */
const convertSeat = (value) => {
  if (value == null || value === '') return value;

  const raw = String(value).trim().toUpperCase();

  // Label → number  ("21A" → 1021, "4B" → 2004)
  const labelMatch = raw.match(/^(-?\d+)?([A-Z])$/);
  if (labelMatch) {
    const numberPart = labelMatch[1] != null ? Number(labelMatch[1]) : 0;
    const letterIndex = labelMatch[2].charCodeAt(0) - 64; // A=1 … Z=26
    return letterIndex * 1000 + numberPart;
  }

  const seatNumber = typeof value === 'number' ? value : Number(raw);
  if (!Number.isFinite(seatNumber)) return value;

  // Plain seat  (-1, 2, 21, …)
  if (seatNumber < 1000) return seatNumber;

  // Number → label  (1021 → "21A", 2004 → "4B")
  const letterIndex = Math.floor(seatNumber / 1000);
  const numberPart = seatNumber % 1000;
  if (letterIndex < 1 || letterIndex > 26) return value;

  const letter = String.fromCharCode(64 + letterIndex);
  if (numberPart === 0) return letter;
  return `${numberPart}${letter}`;
};

export const CORNER_COLOR = {
  topLeft: '#e74c3c',
  topRight: '#2f6fed',
  bottomLeft: '#2e9e4f',
  bottomRight: '#e0b000',
};

/** Colored plus cursor, hotspot at the center. Falls back to the native crosshair. */
export const crosshairCursor = (color) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="${color}" d="M11 2h2v8h8v2h-8v8h-2v-8H3v-2h8z"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 12 12, crosshair`;
};

const { ROVER_MOVE, ROVER_GET_POSITION } = socketEventNames;

const isDisabled = (input, corner, regionType) => {
  if (regionType === 'default' || isNull(regionType)) {
    return ['row', 'seat'].includes(input) && ['topRight', 'bottomLeft'].includes(corner);
  }

  if (regionType === 'singleRow') {
    return ['bottomRight', 'bottomLeft'].includes(corner);
  }

  if (regionType === 'singleSeat') {
    return ['topRight', 'bottomLeft', 'bottomRight'].includes(corner);
  }

  return false;
};

const CornerPosition = ({
  name, path, hidePtzButtons = false, selectCorner = true,
}) => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const pan = useSelector(({ mapping }) => get(mapping, `${path}.pan`));
  const tilt = useSelector(({ mapping }) => get(mapping, `${path}.tilt`));
  const zoom = useSelector(({ mapping: { preview: { zoom: { value } } } }) => value);
  const row = useSelector(({ mapping }) => get(mapping, `${path}.row`));
  const seat = useSelector(({ mapping }) => get(mapping, `${path}.seat`));
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const [tab, corner] = useMemo(() => path.split('.'), []);
  const regionType = useSelector(({ mapping }) => get(mapping, `${tab}.regionType`, null));
  // Encoded letter seats (>= 1000) — input shows the number; label shown beside/tooltip
  let seatAsNumber = NaN;
  if (typeof seat === 'number') {
    seatAsNumber = seat;
  } else if (/^-?\d+$/.test(String(seat ?? '').trim())) {
    seatAsNumber = Number(seat);
  }
  const seatLetterLabel = (Number.isFinite(seatAsNumber) && seatAsNumber >= 1000)
    ? convertSeat(seatAsNumber)
    : null;

  const requestGetPosition = () => {
    dispatch(updatePath('positionPath', corner));
    socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#');
  };

  const sendPositionToCamera = () => {
    console.info('send position to camera');
    let safePan = Number(pan);
    let safeTilt = Number(tilt);
    const safeZoom = Number(zoom / 1000);

    // Map-file pan/tilt are relative to ref; add ref before driving the camera
    // (Check + Map Grid). Rove / Browse / preview keep raw absolute PTZ.
    if (tab === 'check' || tab === 'mapGrid') {
      const camReferencePoint = camMap.getReference();
      safePan += camReferencePoint.pan;
      safeTilt += camReferencePoint.tilt;
    }

    if (Number.isInteger(safePan) && Number.isInteger(safeTilt) && inRange(safeZoom, 0, MAX_ZOOM)) {
      socket.emit(ROVER_MOVE, 'host', connectedCamera, `!${safePan},${safeTilt},${safeZoom}`);

      // After move update preview value
      setTimeout(() => socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#'), 1500);
    } else {
      console.error('Error sending move command to camera', safePan, safeTilt, safeZoom);
    }
  };

  return (
    <div
      className={style.cornerBlock}
      style={CORNER_COLOR[corner] ? { '--corner-accent': CORNER_COLOR[corner] } : undefined}
      onClick={() => {
        if (selectCorner) dispatch(updatePath(`${tab}.activeCorner`, corner));
      }}
      onKeyDown={() => {}}
      role="presentation"
    >
      <div>
        <span>{name}</span>
        {!hidePtzButtons && (
          <div>
            <button
              className={style.circleButton}
              onClick={requestGetPosition}
              type="button"
              title="Get position from camera"
            >
              <FontAwesomeIcon icon={faCircleArrowDown} />
            </button>
            <button
              id={`${corner}-goToPosition`}
              className={style.circleButton}
              onClick={sendPositionToCamera}
              type="button"
              title="Send position to camera"
            >
              <FontAwesomeIcon icon={faCircleArrowUp} />
            </button>
          </div>
        )}
      </div>
      <div>
        <div>
          <span>Pan</span>
          <input
            type="number"
            disabled={isDisabled('pan', corner, regionType)}
            value={pan ?? ''}
            onChange={({ target: { value } }) => dispatch(updateCornerValue(corner, 'pan', value))}
          />
        </div>
        <div>
          <span>Tilt</span>
          <input
            type="number"
            disabled={isDisabled('tilt', corner, regionType)}
            value={tilt ?? ''}
            onChange={({ target: { value } }) => dispatch(updateCornerValue(corner, 'tilt', value))}
          />
        </div>
        <div>
          <span>Row</span>
          <input
            type="text"
            disabled={isDisabled('row', corner, regionType)}
            value={row}
            onChange={({ target: { value } }) => dispatch(updateCornerValue(corner, 'row', value.toUpperCase()))}
          />
        </div>
        <div>
          <span>Seat</span>
          <div style={{
            display: 'flex', width: '70%', alignItems: 'center', gap: 6,
          }}
          >
            <input
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              title={seatLetterLabel
                ? `Stored as ${seatAsNumber} → ${seatLetterLabel}`
                : 'Type 11A then blur to store 1011'}
              style={{ width: '100%', marginBottom: 0 }}
              value={seat ?? ''}
              disabled={isDisabled('seat', corner, regionType)}
              onChange={({ target: { value } }) => {
                // Keep raw text while typing — convert only on blur
                // (eager convert made 1A→1001, then +A → 1001A → 2001 = 1B)
                dispatch(updateCornerValue(corner, 'seat', value));
              }}
              onBlur={({ target: { value } }) => {
                const trimmed = String(value).trim().toUpperCase();
                if (/^(-?\d+)?[A-Z]$/.test(trimmed)) {
                  dispatch(updateCornerValue(corner, 'seat', convertSeat(trimmed)));
                  return;
                }
                if (/^-?\d+$/.test(trimmed)) {
                  dispatch(updateCornerValue(corner, 'seat', Number(trimmed)));
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
            {seatLetterLabel && (
              <span
                title={`Letter seat ${seatLetterLabel}`}
                style={{
                  color: '#e74c3c',
                  fontWeight: 600,
                  fontSize: 13,
                  flexShrink: 0,
                  minWidth: 28,
                }}
              >
                {seatLetterLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(CornerPosition);
